/**
 * 通用并发队列
 * - 并发数控制
 * - 相同 key 请求合并
 * - 支持取消
 */

type Callback<V> = {
  resolve: (value: V) => void;
  reject: (error: Error) => void;
};

export interface ConcurrentQueueOptions<K, V> {
  /** 最大并发数 */
  concurrency: number;
  /** 执行任务的函数 */
  executor: (key: K, signal: AbortSignal) => Promise<V>;
  /** 可选：成功回调（用于缓存等） */
  onSuccess?: (key: K, value: V) => void;
  /** 可选：失败回调 */
  onError?: (key: K, error: Error) => void;
}

export class ConcurrentQueue<K, V> {
  private options: ConcurrentQueueOptions<K, V>;
  private waitingQueue: K[] = [];
  private waitingCallbackMap = new Map<K, Callback<V>[]>();
  private pendingCallbackMap = new Map<K, Callback<V>[]>();
  private pendingAbortMap = new Map<K, AbortController>();
  private currentConcurrent = 0;

  constructor(options: ConcurrentQueueOptions<K, V>) {
    this.options = options;
  }

  /** 加入队列执行 */
  enqueue(key: K, priority: 'high' | 'normal' = 'normal'): Promise<V> {
    return new Promise((resolve, reject) => {
      // 已在 pending 中，复用
      if (this.pendingCallbackMap.has(key)) {
        this.pendingCallbackMap.get(key)?.push({ resolve, reject });
        return;
      }

      // 加入等待队列
      if (!this.waitingCallbackMap.has(key)) {
        this.waitingCallbackMap.set(key, []);
        if (priority === 'high') {
          this.waitingQueue.unshift(key);
        } else {
          this.waitingQueue.push(key);
        }
      }
      this.waitingCallbackMap.get(key)?.push({ resolve, reject });

      this.tryNext();
    });
  }

  /** 取消指定 key 的任务 */
  cancel(key: K): void {
    // 取消 pending 中的请求
    const controller = this.pendingAbortMap.get(key);
    if (controller) {
      controller.abort();
    }
    // 从等待队列移除
    const idx = this.waitingQueue.indexOf(key);
    if (idx !== -1) {
      this.waitingQueue.splice(idx, 1);
      this.waitingCallbackMap.delete(key);
    }
  }

  /** 是否正在处理中 */
  isPending(key: K): boolean {
    return this.pendingCallbackMap.has(key);
  }

  /** 是否在等待队列中 */
  isWaiting(key: K): boolean {
    return this.waitingCallbackMap.has(key);
  }

  private tryNext(): void {
    const { concurrency, executor, onSuccess, onError } = this.options;

    if (this.currentConcurrent >= concurrency || this.waitingQueue.length === 0) {
      return;
    }

    const key = this.waitingQueue.shift()!;
    const callbacks = this.waitingCallbackMap.get(key) || [];
    this.waitingCallbackMap.delete(key);
    this.pendingCallbackMap.set(key, callbacks);

    this.currentConcurrent++;
    const controller = new AbortController();
    this.pendingAbortMap.set(key, controller);

    executor(key, controller.signal)
      .then((value) => {
        onSuccess?.(key, value);
        this.pendingCallbackMap.get(key)?.forEach((cb) => cb.resolve(value));
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          onError?.(key, err);
          this.pendingCallbackMap.get(key)?.forEach((cb) => cb.reject(err));
        }
      })
      .finally(() => {
        this.pendingCallbackMap.delete(key);
        this.pendingAbortMap.delete(key);
        this.currentConcurrent--;
        this.tryNext();
      });

    // 继续尝试填满并发槽
    this.tryNext();
  }
}
